import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import Footer from "@/pages/home/components/Footer";

const STEPS = [
  {
    number: "01",
    icon: "ri-user-settings-line",
    title: "Build Your Capability Profile",
    description: "Skip the formal resume. Select what you can actually do — physical abilities, skills, tools you own, and tech comfort level.",
    highlight: "Takes under 3 minutes",
    color: "blue",
  },
  {
    number: "02",
    icon: "ri-ai-generate",
    title: "AI Matches You Instantly",
    description: "Our engine scans hundreds of local and remote opportunities, ranking them by pay rate, distance, and capability fit in real time.",
    highlight: "Average: 4 min to first match",
    color: "violet",
  },
  {
    number: "03",
    icon: "ri-file-text-line",
    title: "Apply with AI Assistance",
    description: "Auto-generated resumes and cover letters tailored per job. Review and confirm — we handle the rest.",
    highlight: "1-click semi-automated apply",
    color: "green",
  },
  {
    number: "04",
    icon: "ri-money-dollar-circle-line",
    title: "Start Earning Today",
    description: "Accept same-day gigs, track earnings on your dashboard, and get upskill suggestions to unlock higher-paying roles.",
    highlight: "Same-day payout available",
    color: "orange",
  },
];

const colorMap: Record<string, { bg: string; text: string; border: string; num: string }> = {
  blue:   { bg: "bg-blue-50",   text: "text-blue-600",   border: "border-blue-100",   num: "text-blue-200"   },
  violet: { bg: "bg-violet-50", text: "text-violet-600", border: "border-violet-100", num: "text-violet-200" },
  green:  { bg: "bg-green-50",  text: "text-green-600",  border: "border-green-100",  num: "text-green-200"  },
  orange: { bg: "bg-orange-50", text: "text-orange-600", border: "border-orange-100", num: "text-orange-200" },
};

const FAQS = [
  { q: "Do I need a resume to get started?", a: "Nope. Gig n' Cash is built around capabilities, not resumes. Just tell us what you can do and we handle the rest — including generating application materials for you." },
  { q: "How fast will I get matched after building my profile?", a: "Most workers see their first match within 4 minutes of completing their capability profile. Urgency jobs can surface in under 60 seconds." },
  { q: "What if I don't have formal certifications?", a: "That's totally fine. Many jobs on Gig n' Cash only require physical capability and reliability. You can also use our Upskill Engine to earn certifications fast and unlock higher pay." },
  { q: "Is the AI apply really automated?", a: "It's semi-automated — the AI drafts your tailored resume and cover letter, and you review and approve before it's sent. You stay in control." },
];

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-28 pb-16 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-3.5 py-1.5 rounded-full mb-4">
            Simple Process
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
            From Profile to Paycheck<br />
            <span className="text-emerald-500">in Minutes</span>
          </h1>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto mb-8">
            No long forms. No waiting weeks. Gig <span className="italic">n&apos;</span> Cash gets you earning faster than any platform on the market — here's exactly how it works.
          </p>
          <div className="flex flex-wrap gap-4 justify-center text-sm text-gray-500">
            {[
              { icon: "ri-timer-line", text: "Profile setup in ~3 minutes" },
              { icon: "ri-radar-line", text: "First match in ~4 minutes" },
              { icon: "ri-bank-card-line", text: "Same-day payout available" },
            ].map((b) => (
              <div key={b.text} className="flex items-center gap-1.5">
                <i className={`${b.icon} text-emerald-500`} />
                <span>{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((step, i) => {
              const c = colorMap[step.color];
              return (
                <div key={step.number} className={`relative rounded-2xl border ${c.border} ${c.bg} p-6 flex flex-col gap-4`}>
                  <span className={`absolute top-4 right-5 text-5xl font-black ${c.num} select-none`}>{step.number}</span>
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-white border ${c.border}`}>
                    <i className={`${step.icon} text-2xl ${c.text}`} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug">{step.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{step.description}</p>
                  </div>
                  <div className={`mt-auto pt-3 border-t ${c.border}`}>
                    <span className={`text-xs font-semibold ${c.text} flex items-center gap-1.5`}>
                      <i className="ri-sparkling-line" />{step.highlight}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="hidden lg:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 items-center justify-center bg-white rounded-full border border-gray-200">
                      <i className="ri-arrow-right-line text-gray-400 text-sm" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Visual explainer rows */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col gap-16">
          {[
            {
              img: "https://public.readdy.ai/ai/img_res/6d4c2735654f72a62b204875990336ff.jpg",
              title: "No Resume. Just Real Skills.",
              desc: "Forget formatting your CV. Gig n' Cash replaces the resume with a fast, interactive capability profile. Physical abilities, tools you own, availability — built in 3 minutes, not 3 hours.",
              cta: { label: "Build Your Profile", to: "/capabilities" },
              reverse: false,
            },
            {
              img: "https://readdy.ai/api/search-image?query=AI%20matching%20algorithm%20dashboard%20job%20listings%20results%20screen%20mobile%20modern%20minimalist%20clean%20UI%20gig%20economy&width=520&height=380&seq=hiw2&orientation=landscape",
              title: "AI That Works for You, Not Against You.",
              desc: "Our matching engine doesn't just show you jobs — it ranks them by how much you'll earn, how close they are, and how well they fit what you can do. No irrelevant listings, ever.",
              cta: { label: "See Live Jobs", to: "/find-work" },
              reverse: true,
            },
          ].map((row) => (
            <div key={row.title} className={`flex flex-col ${row.reverse ? "lg:flex-row-reverse" : "lg:flex-row"} gap-12 items-center`}>
              <div className="w-full lg:w-[520px] h-72 rounded-2xl overflow-hidden shrink-0">
                <img src={row.img} alt={row.title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">{row.title}</h2>
                <p className="text-gray-500 text-base leading-relaxed mb-6">{row.desc}</p>
                <Link to={row.cta.to} className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 text-white font-bold rounded-xl text-sm hover:bg-emerald-600 transition-colors whitespace-nowrap">
                  {row.cta.label} <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#fafafa] border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h2 className="text-2xl font-extrabold text-gray-900 mb-8 text-center">Quick Answers</h2>
          <div className="flex flex-col gap-4">
            {FAQS.map((faq) => (
              <div key={faq.q} className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="font-bold text-gray-900 text-sm mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-500">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">Your first match is waiting.</h2>
          <p className="text-emerald-100 mb-7 max-w-md mx-auto">Build your profile in 3 minutes and start earning today.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/capabilities" className="px-8 py-3.5 bg-white text-emerald-700 font-bold rounded-xl text-sm hover:bg-emerald-50 transition-colors whitespace-nowrap">
              <i className="ri-flashlight-line mr-2" />Build My Profile
            </Link>
            <Link to="/pricing" className="px-8 py-3.5 bg-white/10 border border-white/20 text-white font-bold rounded-xl text-sm hover:bg-white/20 transition-colors whitespace-nowrap">
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
